const LOG_PREFIX = "[WebSignUp Timing]";
const PAGE_START_MARK = "wl-web-signup-page-start";
const WINNER_LOG_QUERY_PARAM = "winnerlogenabled";

const loggedOnceKeys = new Set<string>();
let cachedWinnerLogEnabled: boolean | null = null;

const isWinnerLogEnabled = (): boolean => {
  if (cachedWinnerLogEnabled !== null) {
    return cachedWinnerLogEnabled;
  }

  try {
    if (typeof window === "undefined") {
      cachedWinnerLogEnabled = false;
      return false;
    }

    cachedWinnerLogEnabled =
      new URLSearchParams(window.location.search).get(WINNER_LOG_QUERY_PARAM) ===
      "true";
  } catch {
    cachedWinnerLogEnabled = false;
  }

  return cachedWinnerLogEnabled;
};

const serializeLoggerError = (error: unknown): string => {
  try {
    if (error instanceof Error) {
      return error.message || error.name || "Unknown Error";
    }

    if (typeof error === "string") {
      return error;
    }

    return String(error);
  } catch {
    return "Unknown logger error";
  }
};

const reportLoggerError = (context: string, error: unknown): void => {
  try {
    if (!isWinnerLogEnabled()) {
      return;
    }

    console.error(`${LOG_PREFIX} logger error (${context})`, {
      message: serializeLoggerError(error),
      error,
    });
  } catch {
    // ignore — never break signup from logger diagnostics
  }
};

const safeNow = (): number => {
  try {
    if (
      typeof performance !== "undefined" &&
      typeof performance.now === "function"
    ) {
      return performance.now();
    }
  } catch (error) {
    reportLoggerError("safeNow", error);
  }

  return Date.now();
};

const getSincePageMs = (): number | null => {
  try {
    if (
      typeof performance === "undefined" ||
      typeof performance.getEntriesByName !== "function"
    ) {
      return null;
    }

    const marks = performance.getEntriesByName(PAGE_START_MARK);
    if (!marks.length) {
      return null;
    }

    return Math.round(safeNow() - marks[0].startTime);
  } catch (error) {
    reportLoggerError("getSincePageMs", error);
    return null;
  }
};

const emitLog = (label: string, details?: Record<string, unknown>): void => {
  if (!isWinnerLogEnabled()) {
    return;
  }

  // Defer so logging never stalls the signup critical path.
  const run = (): void => {
    try {
      console.log(`${LOG_PREFIX} ${label}`, {
        sincePageMs: getSincePageMs(),
        ...details,
      });
    } catch (error) {
      reportLoggerError(`emitLog:${label}`, error);
    }
  };

  try {
    if (typeof queueMicrotask === "function") {
      queueMicrotask(run);
      return;
    }
  } catch (error) {
    reportLoggerError("queueMicrotask", error);
  }

  try {
    setTimeout(run, 0);
  } catch (error) {
    reportLoggerError("setTimeout", error);
    // Last resort: best-effort sync log, still try/caught inside run.
    run();
  }
};

export const markWebSignupPageStart = (): void => {
  if (!isWinnerLogEnabled()) {
    return;
  }

  try {
    if (
      typeof performance !== "undefined" &&
      typeof performance.getEntriesByName === "function" &&
      performance.getEntriesByName(PAGE_START_MARK).length > 0
    ) {
      return;
    }

    if (
      typeof performance !== "undefined" &&
      typeof performance.mark === "function"
    ) {
      performance.mark(PAGE_START_MARK);
    }

    emitLog("page mount");
  } catch (error) {
    reportLoggerError("markWebSignupPageStart", error);
  }
};

export const logWebSignupTiming = (
  label: string,
  details?: Record<string, unknown>,
): void => {
  if (!isWinnerLogEnabled()) {
    return;
  }

  try {
    emitLog(label, details);
  } catch (error) {
    reportLoggerError("logWebSignupTiming", error);
  }
};

export const logWebSignupTimingOnce = (
  key: string,
  label: string,
  details?: Record<string, unknown>,
): void => {
  if (!isWinnerLogEnabled()) {
    return;
  }

  try {
    if (loggedOnceKeys.has(key)) {
      return;
    }

    loggedOnceKeys.add(key);
    emitLog(label, details);
  } catch (error) {
    reportLoggerError("logWebSignupTimingOnce", error);
  }
};

/**
 * Times an async call without letting timing/logging failures affect the result.
 * The wrapped `fn` always runs; errors from `fn` are rethrown unchanged.
 */
export const timeWebSignupAsync = async <T>(
  label: string,
  fn: () => Promise<T>,
  details?: Record<string, unknown>,
): Promise<T> => {
  if (!isWinnerLogEnabled()) {
    return fn();
  }

  let start = 0;
  try {
    start = safeNow();
  } catch (error) {
    reportLoggerError("timeWebSignupAsync:start", error);
    start = Date.now();
  }

  try {
    const result = await fn();
    try {
      emitLog(label, {
        durationMs: Math.round(safeNow() - start),
        ...details,
      });
    } catch (error) {
      reportLoggerError("timeWebSignupAsync:successLog", error);
    }
    return result;
  } catch (error) {
    try {
      emitLog(`${label} (failed)`, {
        durationMs: Math.round(safeNow() - start),
        error: serializeLoggerError(error),
        ...details,
      });
    } catch (loggerError) {
      reportLoggerError("timeWebSignupAsync:failureLog", loggerError);
    }
    throw error;
  }
};

/** Safe callback for shared services — never throws. */
export const safeSignupTimingStep = (
  label: string,
  durationMs: number,
): void => {
  if (!isWinnerLogEnabled()) {
    return;
  }

  try {
    emitLog(label, { durationMs });
  } catch (error) {
    reportLoggerError("safeSignupTimingStep", error);
  }
};
